import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineBookOpen,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineCheckCircle,
  HiOutlineDocumentDuplicate,
} from "react-icons/hi2";
import {
  authService,
  oplService,
  machineService,
  plantService,
  lineService,
  masterDataService,
} from "../services";
import { usePermissions } from "../hooks/usePermissions";
import SharedModal from "../components/common/SharedModal";
import PageLoading from "../components/common/PageLoading";
import "./OPLDocuments.css";

// Helper function to get cached data (defined outside component for synchronous access)
const getCachedOPLData = () => {
  try {
    const cachedDocuments = sessionStorage.getItem("opl_documents_data");
    const cachedMachines = sessionStorage.getItem("machines_data");
    const cachedCategories = sessionStorage.getItem("opl_categories");
    const cachedStatuses = sessionStorage.getItem("opl_statuses");

    return {
      documents: cachedDocuments ? JSON.parse(cachedDocuments) : [],
      machines: cachedMachines ? JSON.parse(cachedMachines) : [],
      categories: cachedCategories ? JSON.parse(cachedCategories) : [],
      statuses: cachedStatuses ? JSON.parse(cachedStatuses) : [],
      hasAll: !!(
        cachedDocuments &&
        cachedMachines &&
        cachedCategories &&
        cachedStatuses
      ),
    };
  } catch (e) {
    return {
      documents: [],
      machines: [],
      categories: [],
      statuses: [],
      hasAll: false,
    };
  }
};

const OPLDocuments = () => {
  // Helper function to safely get statuses by mapping to actual existing backend IDs to prevent 500 API errors
  const getSafeStaticStatuses = (allStatusesData) => {
    if (!allStatusesData || allStatusesData.length === 0) return [];

    const findStatus = (name) => allStatusesData.find(s => s.statusName?.toLowerCase() === name.toLowerCase());

    const draftStatus = findStatus('Draft') || findStatus('Open');
    const pendingStatus = findStatus('Pending Approval') || findStatus('Pending') || findStatus('Submitted') || findStatus('In Progress');
    const approvedStatus = findStatus('Approved') || findStatus('Completed') || findStatus('Closed');

    const safeStatuses = [];
    if (draftStatus) safeStatuses.push({ statusId: draftStatus.statusId, statusName: 'Draft' });
    if (pendingStatus) safeStatuses.push({ statusId: pendingStatus.statusId, statusName: 'Pending Approval' });
    if (approvedStatus) safeStatuses.push({ statusId: approvedStatus.statusId, statusName: 'Approved' });

    return safeStatuses;
  };

  // Initialize state with cached data if available (synchronous)
  const cachedData = getCachedOPLData();
  const [oplDocuments, setOplDocuments] = useState(cachedData.documents);
  const [machines, setMachines] = useState(cachedData.machines);
  const [categories, setCategories] = useState(cachedData.categories);
  const [statuses, setStatuses] = useState(cachedData.statuses);
  const [loading, setLoading] = useState(!cachedData.hasAll);
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [machineFilter, setMachineFilter] = useState("");
  const [plantFilter, setPlantFilter] = useState("");
  const [lineFilter, setLineFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [versioningDocument, setVersioningDocument] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    categoryId: "",
    machineId: "",
    objective: "",
    steps: "",
    statusId: "",
    referenceNumber: null,
    moduleName: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission("OPL.View");
  const canCreate = hasPermission("OPL.Create");
  const canEdit = hasPermission("OPL.Edit");
  const canDelete = hasPermission("OPL.Delete");

  const navigate = useNavigate();
  const location = useLocation();

  // Handle navigation state for creating new OPL
  useEffect(() => {
    if (location.state?.create && canCreate) {
      setFormData((prev) => ({
        ...prev,
        title: location.state.title || prev.title,
        objective: location.state.objective || prev.objective,
        machineId: location.state.machineId || prev.machineId,
        referenceNumber: location.state.referenceNumber || prev.referenceNumber,
        moduleName: location.state.moduleName || prev.moduleName,
      }));
      setShowModal(true);
      // Clear the state to prevent reopening on simple re-renders?
      // We can't easily clear location.state without navigating again,
      // but since this effect depends on location.state, it runs when it changes or mounts.
      // If user refreshes, strict mode might double run, but setting state is idempotent-ish here.
    }
  }, [location.state, canCreate]);

  // Refresh cached data on mount (in case it was updated elsewhere)
  useEffect(() => {
    if (tenantId) {
      const refreshedCachedData = getCachedOPLData();
      if (refreshedCachedData.hasAll) {
        setOplDocuments(refreshedCachedData.documents);
        setMachines(refreshedCachedData.machines);
        setCategories(refreshedCachedData.categories);
        setStatuses(refreshedCachedData.statuses);
        setLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setError("Please login to view OPL documents");
      setLoading(false);
      return;
    }

    // Check if we have cached data - if yes, show page immediately
    const hasCachedData =
      sessionStorage.getItem("opl_documents_data") &&
      sessionStorage.getItem("machines_data") &&
      sessionStorage.getItem("opl_categories") &&
      sessionStorage.getItem("opl_statuses");

    if (hasCachedData) {
      setLoading(false);
      if (canView) {
        loadData();
      }
    }

    if (!canView) {
      setError("You do not have permission to view OPL documents");
      setLoading(false);
      return;
    }

    if (canView && !hasCachedData) {
      loadData();
    }
  }, [tenantId, canView]);

  const loadData = async () => {
    try {
      // Only show loading if we don't have cached data
      const hasCachedData = sessionStorage.getItem("opl_documents_data");
      if (!hasCachedData) {
        setLoading(true);
      }
      setError("");
      const [documentsData, machinesData, categoriesData, statusesData, plantsData, linesData] =
        await Promise.all([
          oplService.getAllOplDocuments(),
          machineService.getAllMachines(),
          masterDataService.getCategories(tenantId),
          masterDataService.getStatuses(tenantId),
          plantService.getAllPlants(),
          lineService.getAllLines(),
        ]);

      setPlants(plantsData || []);
      setLines(linesData || []);

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
        setPlantFilter(plantsData[0].plantId.toString());
      }

      // Filter categories and statuses for OPL type
      const oplCategories = categoriesData.filter(
        (c) => c.categoryType === "OPL",
      );
      let oplStatuses = statusesData.filter((s) => s.statusType === "OPL");

      // Use static fallback mapping to ACTUAL IDs if database doesn't have OPL types defined
      if (oplStatuses.length === 0) {
        oplStatuses = getSafeStaticStatuses(statusesData);
      }

      setOplDocuments(documentsData);
      setMachines(machinesData);
      setCategories(oplCategories);
      setStatuses(oplStatuses);

      // Cache the data
      sessionStorage.setItem(
        "opl_documents_data",
        JSON.stringify(documentsData),
      );
      sessionStorage.setItem("machines_data", JSON.stringify(machinesData));
      sessionStorage.setItem("opl_categories", JSON.stringify(oplCategories));
      sessionStorage.setItem("opl_statuses", JSON.stringify(oplStatuses));
    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleMachineFilter = (e) => {
    setMachineFilter(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const filteredDocuments = oplDocuments.filter((doc) => {
    const matchesSearch = doc.title
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesMachine =
      !machineFilter || doc.machineId?.toString() === machineFilter;
    const matchesStatus =
      !statusFilter || doc.statusId?.toString() === statusFilter;

    let matchesPlant = true;
    if (plantFilter) {
        const machine = machines.find(m => m.machineId === doc.machineId);
        const line = machine ? lines.find(l => l.lineId === machine.lineId) : null;
        matchesPlant = (machine?.plantId?.toString() === plantFilter) || (line?.plantId?.toString() === plantFilter);
    }

    let matchesLine = true;
    if (lineFilter) {
        const machine = machines.find(m => m.machineId === doc.machineId);
        matchesLine = machine?.lineId?.toString() === lineFilter;
    }

    return matchesSearch && matchesMachine && matchesStatus && matchesPlant && matchesLine;
  });

  const handleAdd = () => {
    if (!canCreate) {
      setError("You do not have permission to create OPL documents");
      return;
    }
    setEditingDocument(null);
    setFormData({
      title: "",
      categoryId: "",
      machineId: "",
      objective: "",
      steps: "",
      statusId: "",
      referenceNumber: null,
      moduleName: "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = async (document) => {
    if (!canEdit) {
      setError("You do not have permission to edit OPL documents");
      return;
    }
    try {
      // Fetch full document details
      const fullDoc = await oplService.getOplDocumentById(document.oplId);
      setEditingDocument(fullDoc);
      setFormData({
        title: fullDoc.title || "",
        categoryId: fullDoc.categoryId ? fullDoc.categoryId.toString() : "",
        machineId: fullDoc.machineId ? fullDoc.machineId.toString() : "",
        objective: fullDoc.objective || "",
        steps: fullDoc.steps || "",
        statusId: fullDoc.statusId ? fullDoc.statusId.toString() : "",
        referenceNumber: fullDoc.referenceNumber || null,
        moduleName: fullDoc.moduleName || "",
      });
      setFormErrors({});
      setShowModal(true);
    } catch (err) {
      setError("Failed to load document details. Please try again.");
      console.error("Error loading document:", err);
    }
  };

  const handleCreateVersion = async (document) => {
    if (!canCreate) {
      setError("You do not have permission to create OPL documents");
      return;
    }
    try {
      const fullDoc = await oplService.getOplDocumentById(document.oplId);
      setVersioningDocument(fullDoc);
      setFormData({
        title: fullDoc.title || "",
        categoryId: fullDoc.categoryId || "",
        machineId: fullDoc.machineId || "",
        objective: fullDoc.objective || "",
        steps: fullDoc.steps || "",
        statusId: fullDoc.statusId || "",
      });
      setFormErrors({});
      setShowVersionModal(true);
    } catch (err) {
      setError("Failed to load document for versioning. Please try again.");
      console.error("Error loading document:", err);
    }
  };

  const handleApprove = async (id) => {
    try {
      setApprovingId(id);
      setError("");
      await oplService.approveOplDocument(id);
      await loadData();
    } catch (err) {
      setError("Failed to approve document. Please try again.");
      console.error("Error approving document:", err);
    } finally {
      setApprovingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      setError("You do not have permission to delete OPL documents");
      return;
    }
    try {
      setError("");
      await oplService.deleteOplDocument(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete document. Please try again.");
      console.error("Error deleting document:", err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = "Title is required";
    } else if (formData.title.length > 150) {
      errors.title = "Title must be less than 150 characters";
    }
    if (!formData.objective.trim()) {
      errors.objective = "Objective is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (editingDocument && !canEdit) {
      setError("You do not have permission to edit OPL documents");
      return;
    }
    if (!editingDocument && !canCreate) {
      setError("You do not have permission to create OPL documents");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const oplData = {
        title: formData.title.trim(),
        categoryId:
          formData.categoryId && formData.categoryId !== ""
            ? parseInt(formData.categoryId, 10)
            : null,
        machineId:
          formData.machineId && formData.machineId !== ""
            ? parseInt(formData.machineId, 10)
            : null,
        objective: formData.objective.trim(),
        steps:
          formData.steps && formData.steps.trim()
            ? formData.steps.trim()
            : null,
        statusId:
          formData.statusId && formData.statusId !== ""
            ? parseInt(formData.statusId, 10)
            : null,
        referenceNumber: formData.referenceNumber,
        moduleName: formData.moduleName,
      };

      if (editingDocument) {
        oplData.oplId = editingDocument.oplId;
        // Preserve version when updating - backend expects this field
        oplData.version = editingDocument.version || 1;
        await oplService.updateOplDocument(editingDocument.oplId, oplData);
      } else {
        await oplService.createOplDocument(oplData);
      }

      await loadData();
      setShowModal(false);
      setFormData({
        title: "",
        categoryId: "",
        machineId: "",
        objective: "",
        steps: "",
        statusId: "",
      });
      setEditingDocument(null);
    } catch (err) {
      console.error("Error saving OPL document:", err);
      const errorMessage =
        err.response?.data?.message ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        (err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(", ")
          : null) ||
        err.message ||
        "Failed to save document. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVersionSubmit = async (e) => {
    e.preventDefault();

    if (!canCreate) {
      setError("You do not have permission to create OPL documents");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const newVersionData = {
        title: formData.title.trim(),
        categoryId:
          formData.categoryId && formData.categoryId !== ""
            ? parseInt(formData.categoryId, 10)
            : null,
        machineId:
          formData.machineId && formData.machineId !== ""
            ? parseInt(formData.machineId, 10)
            : null,
        objective: formData.objective.trim(),
        steps:
          formData.steps && formData.steps.trim()
            ? formData.steps.trim()
            : null,
        statusId:
          formData.statusId && formData.statusId !== ""
            ? parseInt(formData.statusId, 10)
            : null,
      };

      await oplService.createNewVersion(
        versioningDocument.oplId,
        newVersionData,
      );

      await loadData();
      setShowVersionModal(false);
      setFormData({
        title: "",
        categoryId: "",
        machineId: "",
        objective: "",
        steps: "",
        statusId: "",
      });
      setVersioningDocument(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        (err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(", ")
          : null) ||
        err.message ||
        "Failed to create new version. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDocument(null);
    setFormData({
      title: "",
      categoryId: "",
      machineId: "",
      objective: "",
      steps: "",
      statusId: "",
    });
    setFormErrors({});
  };

  const handleCloseVersionModal = () => {
    setShowVersionModal(false);
    setVersioningDocument(null);
    setFormData({
      title: "",
      categoryId: "",
      machineId: "",
      objective: "",
      steps: "",
      statusId: "",
    });
    setFormErrors({});
  };

  const getMachineName = (machineId) => {
    if (!machineId) return "-";
    const machine = machines.find((m) => m.machineId === machineId);
    return machine ? machine.machineName : "-";
  };

  const getCategoryName = (categoryId) => {
    if (!categoryId) return "-";
    const category = categories.find((c) => c.categoryId === categoryId);
    return category ? category.categoryName : "-";
  };

  const getStatusName = (statusId) => {
    if (!statusId) return "-";
    const status = statuses.find((s) => s.statusId === statusId);
    return status ? status.statusName : "-";
  };

  if (loading) {
    return <PageLoading message="Loading OPL documents..." />;
  }

  return (
    <div className="pagecontainer oplpage">
      <div className="page-header">
        <div className="header-content">
          <h1>OPL Documents</h1>
          <p>Manage One Point Lesson documents with version control</p>
        </div>

        {canCreate && (
          <div className="header-actions">
            <button className="btn-primary btn" onClick={handleAdd}>
              <HiOutlinePlus />
              Add OPL Document
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {
        canView && (
          <div className="table-container pad10 kizantable">
            <div className="table-header stacked opldocument">
              <div className="left-group">
                <div className="search-box">
                  <HiOutlineMagnifyingGlass className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search OPL documents..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="search-input"
                  />
                </div>
                <div className="filter-group">
                  <select
                    className="filter-select"
                    value={plantFilter}
                    onChange={(e) => {
                        const val = e.target.value;
                        setPlantFilter(val);
                        setLineFilter("");
                        setMachineFilter("");

                    }}
                  >
                    <option value="">All Plants</option>
                    {plants.map((p) => (
                      <option key={p.plantId} value={p.plantId}>
                        {p.plantName}
                      </option>
                    ))}
                  </select>
                  <select
                    className="filter-select"
                    value={lineFilter}
                    onChange={(e) => {
                        setLineFilter(e.target.value);
                        setMachineFilter("");
                    }}
                    disabled={!plantFilter}
                  >
                    <option value="">All Lines</option>
                    {lines.filter(l => !plantFilter || l.plantId?.toString() === plantFilter).map((l) => (
                      <option key={l.lineId} value={l.lineId}>
                        {l.lineName}
                      </option>
                    ))}
                  </select>
                  <select
                    className="filter-select"
                    value={machineFilter}
                    onChange={handleMachineFilter}
                    disabled={!plantFilter && !lineFilter}
                  >
                    <option value="">All Machines</option>
                    {machines.filter(m => {
                        if (lineFilter) return m.lineId?.toString() === lineFilter;
                        if (plantFilter) return lines.find(l => l.lineId === m.lineId)?.plantId?.toString() === plantFilter;
                        return true;
                    }).map((machine) => (
                      <option key={machine.machineId} value={machine.machineId}>
                        {machine.machineName}
                      </option>
                    ))}
                  </select>
                  <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={handleStatusFilter}
                  >
                    <option value="">All Statuses</option>
                    {statuses.map((status) => (
                      <option key={status.statusId} value={status.statusId}>
                        {status.statusName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="table-info">
                {filteredDocuments.length}{" "}
                {filteredDocuments.length === 1 ? "document" : "documents"}
              </div>
            </div>

            {filteredDocuments.length === 0 ? (
              <div className="empty-state">
                <HiOutlineBookOpen className="empty-icon" />
                <h3>No OPL documents found</h3>
                <p>
                  {searchTerm || machineFilter || statusFilter
                    ? "Try adjusting your search or filter terms"
                    : canCreate
                      ? "Get started by creating your first OPL document"
                      : "No OPL documents found"}
                </p>
                {!searchTerm && !machineFilter && !statusFilter && canCreate && (
                  <button className="btn-primary btn" onClick={handleAdd}>
                    Add OPL Document
                  </button>
                )}
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Machine</th>
                    <th>Reference</th>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Approved</th>
                    <th style={{ width: "100px" }}>Created At</th>
                    <th style={{ width: "150px", textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.oplId}>
                      <td>{doc.oplId}</td>
                      <td className="name-cell">{doc.title}</td>
                      <td>{getCategoryName(doc.categoryId)}</td>
                      <td>{getMachineName(doc.machineId)}</td>
                      <td>
                        {doc.referenceNumber ? (
                          <span
                            className={`reference-badge ${doc.moduleName === "FiveWhys" ? "clickable" : ""}`}
                            onClick={(e) => {
                              if (doc.moduleName === "FiveWhys") {
                                e.stopPropagation();
                                navigate(
                                  `/home/rca/five-whys?rcaId=${doc.referenceNumber}`,
                                );
                              }
                            }}
                            title={
                              doc.moduleName === "FiveWhys"
                                ? "View Five Whys Analysis"
                                : ""
                            }
                          >
                            #{doc.referenceNumber}{" "}
                            {doc.moduleName ? `- ${doc.moduleName}` : ""}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <span className="version-badge">v{doc.version}</span>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${getStatusName(doc.statusId)?.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {getStatusName(doc.statusId)}
                        </span>
                      </td>
                      <td>
                        {doc.approvedBy ? (
                          <span className="approved-badge">Yes</span>
                        ) : (
                          <span className="not-approved-badge">No</span>
                        )}
                      </td>
                      <td>
                        {doc.createdAt
                          ? new Date(doc.createdAt).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>
                        <div className="action-buttons">
                          {canEdit && (
                            <button
                              className="btn-icon-edit"
                              onClick={() => handleEdit(doc)}
                              title="Edit"
                            >
                              <HiOutlinePencil />
                            </button>
                          )}
                          {canCreate && (
                            <button
                              className="btn-icon-version"
                              onClick={() => handleCreateVersion(doc)}
                              title="Create New Version"
                            >
                              <HiOutlineDocumentDuplicate />
                            </button>
                          )}
                          {!doc.approvedBy && (
                            <button
                              className="btn-icon-approve"
                              onClick={() => handleApprove(doc.oplId)}
                              title="Approve"
                              disabled={approvingId === doc.oplId}
                            >
                              <HiOutlineCheckCircle />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn-icon-delete"
                              onClick={() => setDeleteConfirm(doc)}
                              title="Delete"
                            >
                              <HiOutlineTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )
      }
      {
        !canView && (
          <div className="error-message">
            You do not have permission to view OPL documents
          </div>
        )
      }

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingDocument ? "Edit OPL Document" : "Add New OPL Document"}
        customWidth="800px"
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="opl-form"
              className="btn-primary"
              disabled={submitting || (editingDocument ? !canEdit : !canCreate)}
            >
              {submitting
                ? editingDocument
                  ? "Updating..."
                  : "Creating..."
                : editingDocument
                ? "Update Document"
                : "Create Document"}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <form id="opl-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="title" className="form-label required">Title</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className={formErrors.title ? "error" : ""}
                placeholder="Enter OPL document title"
                maxLength={150}
                disabled={editingDocument ? !canEdit : !canCreate}
              />
              {formErrors.title && (
                <span className="field-error">{formErrors.title}</span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="categoryId" className="form-label">Category</label>
                <select
                  id="categoryId"
                  value={formData.categoryId}
                  onChange={(e) =>
                    setFormData({ ...formData, categoryId: e.target.value })
                  }
                  disabled={editingDocument ? !canEdit : !canCreate}
                >
                  <option value="">Select a category (optional)</option>
                  {categories.map((category) => (
                    <option
                      key={category.categoryId}
                      value={category.categoryId}
                    >
                      {category.categoryName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="machineId" className="form-label">Machine</label>
                <select
                  id="machineId"
                  value={formData.machineId}
                  onChange={(e) =>
                    setFormData({ ...formData, machineId: e.target.value })
                  }
                  disabled={editingDocument ? !canEdit : !canCreate}
                >
                  <option value="">Select a machine (optional)</option>
                  {machines.map((machine) => (
                    <option key={machine.machineId} value={machine.machineId}>
                      {machine.machineName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="objective" className="form-label required">Objective</label>
                <textarea
                  id="objective"
                  value={formData.objective}
                  onChange={(e) =>
                    setFormData({ ...formData, objective: e.target.value })
                  }
                  className={formErrors.objective ? "error" : ""}
                  placeholder="Enter the objective..."
                  rows={3}
                  disabled={editingDocument ? !canEdit : !canCreate}
                />
                {formErrors.objective && (
                  <span className="field-error">{formErrors.objective}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="steps" className="form-label">Steps (Optional)</label>
                <textarea
                  id="steps"
                  value={formData.steps}
                  onChange={(e) =>
                    setFormData({ ...formData, steps: e.target.value })
                  }
                  placeholder="Enter the steps or procedure..."
                  rows={3}
                  disabled={editingDocument ? !canEdit : !canCreate}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="statusId" className="form-label">Status</label>
              <select
                id="statusId"
                value={formData.statusId}
                onChange={(e) =>
                  setFormData({ ...formData, statusId: e.target.value })
                }
                disabled={editingDocument ? !canEdit : !canCreate}
              >
                <option value="">Select a status (optional)</option>
                {statuses.map((status) => (
                  <option key={status.statusId} value={status.statusId}>
                    {status.statusName}
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>
      </SharedModal>

      <SharedModal
        isOpen={showVersionModal && !!versioningDocument}
        onClose={handleCloseVersionModal}
        title={`Create New Version - v${versioningDocument?.version + 1}`}
        customWidth="800px" // "large" equivalent
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseVersionModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="opl-version-form"
              className="btn-primary"
              disabled={submitting || !canCreate}
            >
              {submitting ? "Versioning..." : "Create New Version"}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <form id="opl-version-form" onSubmit={handleVersionSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="version-title" className="form-label required">Title</label>
              <input
                type="text"
                id="version-title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className={formErrors.title ? "error" : ""}
                placeholder="Enter OPL document title"
                maxLength={150}
                disabled={!canCreate}
              />
              {formErrors.title && (
                <span className="field-error">{formErrors.title}</span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="version-categoryId" className="form-label">Category</label>
                <select
                  id="version-categoryId"
                  value={formData.categoryId}
                  onChange={(e) =>
                    setFormData({ ...formData, categoryId: e.target.value })
                  }
                  disabled={!canCreate}
                >
                  <option value="">Select a category (optional)</option>
                  {categories.map((category) => (
                    <option
                      key={category.categoryId}
                      value={category.categoryId}
                    >
                      {category.categoryName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="version-machineId" className="form-label">Machine</label>
                <select
                  id="version-machineId"
                  value={formData.machineId}
                  onChange={(e) =>
                    setFormData({ ...formData, machineId: e.target.value })
                  }
                  disabled={!canCreate}
                >
                  <option value="">Select a machine (optional)</option>
                  {machines.map((machine) => (
                    <option key={machine.machineId} value={machine.machineId}>
                      {machine.machineName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="version-objective" className="form-label required">Objective</label>
                <textarea
                  id="version-objective"
                  value={formData.objective}
                  onChange={(e) =>
                    setFormData({ ...formData, objective: e.target.value })
                  }
                  className={formErrors.objective ? "error" : ""}
                  placeholder="Enter the objective..."
                  rows={3}
                  disabled={!canCreate}
                />
                {formErrors.objective && (
                  <span className="field-error">{formErrors.objective}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="version-steps" className="form-label">Steps (Optional)</label>
                <textarea
                  id="version-steps"
                  value={formData.steps}
                  onChange={(e) =>
                    setFormData({ ...formData, steps: e.target.value })
                  }
                  placeholder="Enter the steps or procedure..."
                  rows={3}
                  disabled={!canCreate}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="version-statusId" className="form-label">Status</label>
              <select
                id="version-statusId"
                value={formData.statusId}
                onChange={(e) =>
                  setFormData({ ...formData, statusId: e.target.value })
                }
                disabled={!canCreate}
              >
                <option value="">Select a status (optional)</option>
                {statuses.map((status) => (
                  <option key={status.statusId} value={status.statusId}>
                    {status.statusName}
                  </option>
                ))}
              </select>
            </div>

          </form>
        </div>
      </SharedModal>

      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customWidth="500px"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm.oplId)}
              disabled={!canDelete}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.title}</strong> (v{deleteConfirm?.version})?
          </p>
          <p className="warning-text">
            This action cannot be undone. The OPL document will be
            permanently deleted.
          </p>
        </div>
      </SharedModal>
    </div >
  );
};

export default OPLDocuments;
